import React, { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useModal } from "../../../Store/modules/modal/modal";
import { Div, Text } from "../../Kit";
import { useTheme } from "../../../Store/modules/theme";

export type ModalProps = {
  modalName: string;
  children: ReactNode;
  modalClassName?: string;
  preventToClose?: boolean;
  showCloseIcon?: boolean;
  ignorePadding?: boolean | string;
  onCloseAction?: () => void;
  containerClassName?: string;
};

export const Modal: React.FC<ModalProps> = ({
  modalName,
  children,
  modalClassName = "",
  preventToClose,
  showCloseIcon = false,
  ignorePadding = false,
  onCloseAction,
  containerClassName = "overflow-y-auto",
}) => {
  const { name, dispatchCloseModal } = useModal();
  const open = name === modalName;
  const { name: themeName } = useTheme();
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    if (!preventToClose) dispatchCloseModal();
  };

  const handleCloseIcon = () => {
    onCloseAction?.();
    dispatchCloseModal();
  };

  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.body.style.overflow = "hidden";
    // document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (!open || preventToClose) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [open, preventToClose]);

  const modalRoot =
    document.getElementById("modal-root") ||
    (() => {
      const el = document.createElement("div");
      el.id = "modal-root";
      document.body.appendChild(el);
      return el;
    })();

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) handleClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={overlayRef}
          onClick={onOverlayClick}
          className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={`relative  max-h-[90vh] mx-auto overflow-hidden ${modalClassName} pointer-events-auto`}
          >
            <Div
              bg={themeName === "light" ? "white" : "secondary"}
              className={`relative flex flex-col box-border rounded-[5px] min-w-0 h-full ${
                ignorePadding === false ? "py-[15px] px-[20px]" : ignorePadding
              }`}
            >
              {showCloseIcon && (
                <Text
                  elementType="span"
                  onClick={handleCloseIcon}
                  fontWeight={100}
                  fontSize={28}
                  className="absolute top-1 right-3 pointer text-gray-500 hover:text-gray-700"
                >
                  ×
                </Text>
              )}
              <div
                className={`w-full flex-1 min-h-0 overflow-y-auto hide-scrollbar ${containerClassName}`}
              >
                {children}
              </div>
            </Div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    modalRoot,
  );
};
